import { useState, useEffect } from "react"
import { withRouter, useLocation } from "react-router-dom"
import { connect } from "react-redux"
import { loginUser } from "../../store/actions"
import MetaTags from "react-meta-tags"
import {
  LockOutlined,
  UserOutlined,
  LoginOutlined,
  SafetyOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  EyeOutlined,
} from "@ant-design/icons"
import {
  Form,
  Input,
  Button,
  Card,
  Alert,
  Checkbox,
  Row,
  Col,
  Typography,
  Space,
  Divider,
  Image,
} from "antd"
import logoSm from "../../assets/images/123456.jpg"
import "./auth.scss"

const { Title, Text } = Typography

const Login = ({ loginUser, error, loading, history }) => {
  const location = useLocation()
  const [form] = Form.useForm()
  const [showLogin, setShowLogin] = useState(false) // Toggle login visibility

  // Initial values from URL
  useEffect(() => {
    const query = new URLSearchParams(location.search)
    const initialValues = {
      userName: query.get("u") || "",
      password: query.get("p") || "",
    }
    form.setFieldsValue(initialValues)
  }, [location.search, form])

  const handleSubmit = async values => {
    try {
      await loginUser(values, history)
    } catch (err) {
      console.error("Login failed:", err)
    }
  }

  // Toggle login visibility — only if not already shown
  const toggleLogin = () => {
    setShowLogin(!showLogin)
  }

  return (
    <div className="auth-container">
      <MetaTags>
        <title>ورود | واحد گاز - نسخه 3.1.0</title>
      </MetaTags>

      {/* Animated Background */}
      <div className="auth-background">
        <div className="auth-gradient-circle circle-1" />
        <div className="auth-gradient-circle circle-2" />
        <div className="auth-gradient-circle circle-3" />
      </div>

      <Row justify="center" align="middle" className="auth-row">
        <Col xs={22} sm={18} md={14} lg={10} xl={8} xxl={6}>
          <Card className="auth-card" bordered={false}>
            {/* Show image first */}
            {!showLogin && (
              <div className="auth-image-section">
                <Image
                  src={logoSm}
                  alt="Logo"
                  className="auth-logo main-logo"
                  style={{
                    width: "100%",
                    height: "auto",
                    objectFit: "contain",
                  }}
                  preview={{
                    mask: <EyeOutlined />,
                  }}
                />
                <div className="auth-image-text">
                  <Title level={3} className="auth-title">
                    سامانه واحد گاز استان کردستان
                  </Title>
                  <Space>
                    <Text type="secondary" className="version-tag">
                      نسخه 3.1.0
                    </Text>
                    <Text type="secondary">|</Text>
                    <Text type="secondary">1404</Text>
                  </Space>
                </div>
                <div className="auth-button-container">
                  <Button
                    type="primary"
                    block
                    className="auth-show-login-btn"
                    onClick={toggleLogin}
                    loading={loading}
                  >
                    ورود به سیستم
                  </Button>
                </div>
              </div>
            )}

            {/* Show login form if shown */}
            {showLogin && (
              <div className="auth-form-section">
                {error && (
                  <Alert
                    message={error}
                    type="error"
                    showIcon
                    closable
                    className="auth-alert"
                  />
                )}

                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleSubmit}
                  size="large"
                  className="auth-form"
                >
                  <Form.Item
                    name="userName"
                    label="نام کاربری"
                    rules={[
                      {
                        required: true,
                        message: "لطفاً نام کاربری را وارد کنید",
                      },
                    ]}
                  >
                    <Input
                      prefix={<UserOutlined className="input-icon" />}
                      placeholder="نام کاربری یا ایمیل"
                      autoComplete="username"
                    />
                  </Form.Item>

                  <Form.Item
                    name="password"
                    label="رمز عبور"
                    rules={[
                      {
                        required: true,
                        message: "لطفاً رمز عبور را وارد کنید",
                      },
                    ]}
                  >
                    <Input.Password
                      prefix={<LockOutlined className="input-icon" />}
                      placeholder="رمز عبور"
                      iconRender={visible =>
                        visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                      }
                      autoComplete="current-password"
                    />
                  </Form.Item>

                  <Row
                    justify="space-between"
                    align="middle"
                    className="form-options"
                  >
                    <Form.Item name="remember" valuePropName="checked" noStyle>
                      <Checkbox>مرا به خاطر بسپار</Checkbox>
                    </Form.Item>
                    <Button
                      type="link"
                      href="/forgot-password"
                      className="forgot-link"
                    >
                      فراموشی رمز عبور
                    </Button>
                  </Row>

                  <Form.Item className="submit-section">
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      icon={<LoginOutlined />}
                      block
                      className="auth-submit-btn"
                    >
                      {loading ? "در حال ورود..." : "ورود به سیستم"}
                    </Button>
                  </Form.Item>
                </Form>

                {/* Security Badge */}
                <div className="security-badge">
                  <SafetyOutlined className="security-icon" />
                  <Text type="secondary">اتصال امن با رمزنگاری SSL</Text>
                </div>

                <Divider className="footer-divider" />

                {/* Trust Seal */}
                <div className="trust-section">
                  <a
                    referrerPolicy="origin"
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://trustseal.enamad.ir/?id=293437&Code=nHJ5DfcRppVkF57irimx"
                  >
                    <img
                      referrerPolicy="origin"
                      src="https://Trustseal.eNamad.ir/logo.aspx?id=293437&Code=nHJ5DfcRppVkF57irimx"
                      alt="نماد اعتماد"
                      className="trust-seal"
                      id="nHJ5DfcRppVkF57irimx"
                    />
                  </a>
                </div>

                {/* Copyright */}
                <Text type="secondary" className="copyright">
                  © {new Date().getFullYear()} واحد گاز کردستان - تمامی حقوق
                  محفوظ است
                </Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  )
}

const mapStateToProps = state => ({
  error: state.Login.error,
  loading: state.Login.loading,
})

export default withRouter(connect(mapStateToProps, { loginUser })(Login))
